import { useEffect } from 'react';
import { SpaceNavigationRoot } from 'shared';

import { dataRoomInviteStore } from '../../../storage/local/index';
import { SidebarTreeStore } from '../store/sidebar-tree.store';

type Props = {
  // Nothing is decided while the navigation is still loading: an absent room is not a missing one.
  areRootsLoaded: boolean;
  roots: SpaceNavigationRoot[];
};

/**
 * Opens the room an invitation was just accepted for.
 *
 * The auth guard accepts the token and knows the room's id and nothing else about it, so it sends
 * the person to Home and leaves the id behind in the store; a room's full identity lives in the
 * navigation, and the room is opened once that has arrived.
 *
 * It sits beside `useRoomFromLink`, on the page itself rather than inside either Home panel. The
 * owner's panel and the recipient's are different components, and holding this in the owner's
 * meant a recipient — the person invitations are actually for — accepted theirs and was left
 * standing on Home.
 *
 * The stored invitation is spent only when the room is found. A cached list from an earlier visit
 * can be served before the refetch lands, and burning the one attempt against it would lose the
 * room for good.
 */
export const useAcceptedInviteRoom = ({ areRootsLoaded, roots }: Props): void => {
  const reveal = SidebarTreeStore((state) => state.reveal);

  useEffect(() => {
    const dataRoomId = dataRoomInviteStore.get()?.dataRoomId;

    if (!dataRoomId || !areRootsLoaded) return;

    const room = roots.find(({ id }) => id === dataRoomId);

    if (!room) return;

    dataRoomInviteStore.delete();
    reveal({ breadcrumbs: [], folderId: null, space: room });
  }, [areRootsLoaded, reveal, roots]);
};
